import { type FC, useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useWorkspaceStore } from '@/stores/workspace.store';
import canvasesApi from '@/api/canvases.api';
import workspacesApi from '@/api/workspaces.api';
import type { CanvasSummary } from '@/types/canvas.types';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import ConfirmDialog from '@/components/ui/ConfirmDialog';
import MembersPanel from '@/components/workspace/MembersPanel';
import ImportCanvasButton from '@/components/workspace/ImportCanvasButton';
import ImportPlanActionButton from '@/components/workspace/ImportPlanActionButton';

export const WorkspacePage: FC = () => {
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const { currentWorkspace, selectWorkspace } = useWorkspaceStore();
  const [canvases, setCanvases] = useState<CanvasSummary[]>([]);
  const [isLoadingCanvases, setIsLoadingCanvases] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [activeTab, setActiveTab] = useState<'canvases' | 'members'>('canvases');
  const [deleteCanvasId, setDeleteCanvasId] = useState<string | null>(null);
  const [deleteWsOpen, setDeleteWsOpen] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (workspaceId) {
      selectWorkspace(workspaceId);
      loadCanvases(workspaceId);
    }
  }, [workspaceId, selectWorkspace]);

  const loadCanvases = async (wsId: string) => {
    setIsLoadingCanvases(true);
    try {
      const data = await canvasesApi.listByWorkspace(wsId);
      setCanvases(data);
    } catch {
      // Error handling can be expanded later
    } finally {
      setIsLoadingCanvases(false);
    }
  };

  const handleDeleteCanvas = async () => {
    if (!deleteCanvasId) return;
    setDeleteBusy(true);
    try {
      await canvasesApi.delete(deleteCanvasId);
      setCanvases((prev) => prev.filter((c) => c.id !== deleteCanvasId));
    } finally {
      setDeleteBusy(false);
      setDeleteCanvasId(null);
    }
  };

  const handleDeleteWorkspace = async () => {
    if (!workspaceId) return;
    setDeleteBusy(true);
    try {
      await workspacesApi.delete(workspaceId);
      navigate('/');
    } finally {
      setDeleteBusy(false);
      setDeleteWsOpen(false);
    }
  };

  const deleteCanvasTarget = canvases.find((c) => c.id === deleteCanvasId);

  const handleCreate = async (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !workspaceId) return;
    const canvas = await canvasesApi.create({
      name: name.trim(),
      description: description.trim() || undefined,
      workspaceId,
    });
    setCanvases((prev) => [
      ...prev,
      {
        id: canvas.id,
        name: canvas.name,
        description: canvas.description,
        workspaceId: canvas.workspaceId,
        createdAt: canvas.createdAt,
        updatedAt: canvas.updatedAt,
      },
    ]);
    setName('');
    setDescription('');
    setShowForm(false);
  };

  return (
    <div style={{ maxWidth: 800, margin: '0 auto' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 'var(--spacing-xl)',
        }}
      >
        <div>
          <h1 style={{ fontSize: 'var(--font-size-xl)', fontWeight: 700 }}>
            {currentWorkspace?.name || 'Workspace'}
          </h1>
          {currentWorkspace?.description && (
            <p
              style={{
                fontSize: 'var(--font-size-sm)',
                color: 'var(--color-text-muted)',
                marginTop: 'var(--spacing-xs)',
              }}
            >
              {currentWorkspace.description}
            </p>
          )}
        </div>
        <div style={{ display: 'flex', gap: 'var(--spacing-sm)', alignItems: 'center' }}>
          {workspaceId && (
            <>
              <ImportCanvasButton
                workspaceId={workspaceId}
                onImported={(canvas) =>
                  setCanvases((prev) => [
                    ...prev,
                    {
                      id: canvas.id,
                      name: canvas.name,
                      description: canvas.description,
                      workspaceId: canvas.workspaceId,
                      createdAt: canvas.createdAt,
                      updatedAt: canvas.updatedAt,
                    },
                  ])
                }
              />
              <ImportPlanActionButton
                workspaceId={workspaceId}
                onImported={({
                  canvas,
                  visionCount,
                  objectiveCount,
                  actionCount,
                  stakeholderCount,
                  linkCount,
                }) => {
                  setCanvases((prev) => [
                    ...prev,
                    {
                      id: canvas.id,
                      name: canvas.name,
                      description: canvas.description,
                      workspaceId: canvas.workspaceId,
                      createdAt: canvas.createdAt,
                      updatedAt: canvas.updatedAt,
                    },
                  ]);
                  alert(
                    `Import réussi :\n` +
                      `• ${visionCount} vision(s) (thèmes)\n` +
                      `• ${objectiveCount} objectif(s)\n` +
                      `• ${actionCount} action(s)\n` +
                      `• ${stakeholderCount} partie(s) prenante(s)\n` +
                      `• ${linkCount} liaison(s)`,
                  );
                }}
              />
            </>
          )}
          <Button onClick={() => setShowForm(!showForm)}>
            {showForm ? 'Annuler' : 'Nouveau canvas'}
          </Button>
          <Button variant="danger" onClick={() => setDeleteWsOpen(true)}>
            Supprimer le workspace
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div
        style={{
          display: 'flex',
          gap: 'var(--spacing-xs)',
          marginBottom: 'var(--spacing-lg)',
          borderBottom: '1px solid var(--color-border)',
        }}
      >
        {(['canvases', 'members'] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              padding: 'var(--spacing-sm) var(--spacing-md)',
              backgroundColor: 'transparent',
              border: 'none',
              borderBottom: activeTab === tab ? '2px solid var(--color-primary)' : '2px solid transparent',
              color: activeTab === tab ? 'var(--color-text)' : 'var(--color-text-muted)',
              fontWeight: activeTab === tab ? 600 : 400,
              fontSize: 'var(--font-size-md)',
              cursor: 'pointer',
              transition: 'var(--transition-fast)',
            }}
          >
            {tab === 'canvases' ? 'Canvas' : 'Membres'}
          </button>
        ))}
      </div>

      {activeTab === 'members' && workspaceId && (
        <MembersPanel workspaceId={workspaceId} />
      )}

      {activeTab === 'canvases' && showForm && (
        <form
          onSubmit={handleCreate}
          style={{
            padding: 'var(--spacing-lg)',
            backgroundColor: 'var(--color-surface)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-lg)',
            marginBottom: 'var(--spacing-lg)',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--spacing-md)',
          }}
        >
          <Input
            label="Canvas name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Q2 Strategy Map"
            required
          />
          <Input
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Optional description"
          />
          <Button type="submit" style={{ alignSelf: 'flex-start' }}>
            Create canvas
          </Button>
        </form>
      )}

      {activeTab === 'canvases' && isLoadingCanvases && (
        <p style={{ color: 'var(--color-text-muted)' }}>Chargement des canvas...</p>
      )}

      {activeTab === 'canvases' && !isLoadingCanvases && canvases.length === 0 && (
        <div
          style={{
            textAlign: 'center',
            padding: 'var(--spacing-2xl)',
            color: 'var(--color-text-muted)',
          }}
        >
          <p style={{ fontSize: 'var(--font-size-lg)', marginBottom: 'var(--spacing-sm)' }}>
            Aucun canvas
          </p>
          <p>Creez votre premier canvas pour commencer.</p>
        </div>
      )}

      {activeTab === 'canvases' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 'var(--spacing-md)' }}>
          {canvases.map((canvas) => (
            <div key={canvas.id} style={{ position: 'relative' }}>
              <Link
                to={`/canvases/${canvas.id}`}
                style={{
                  display: 'block',
                  padding: 'var(--spacing-md) var(--spacing-lg)',
                  backgroundColor: 'var(--color-surface)',
                  border: '1px solid var(--color-border)',
                  borderRadius: 'var(--radius-lg)',
                  textDecoration: 'none',
                  transition: 'var(--transition-fast)',
                }}
              >
                <h3
                  style={{
                    fontSize: 'var(--font-size-md)',
                    fontWeight: 600,
                    color: 'var(--color-text)',
                    marginBottom: 'var(--spacing-xs)',
                    paddingRight: 28,
                  }}
                >
                  {canvas.name}
                </h3>
                {canvas.description && (
                  <p
                    style={{
                      fontSize: 'var(--font-size-sm)',
                      color: 'var(--color-text-muted)',
                      marginBottom: 'var(--spacing-sm)',
                    }}
                  >
                    {canvas.description}
                  </p>
                )}
                <div
                  style={{
                    display: 'flex',
                    gap: 'var(--spacing-md)',
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--color-text-muted)',
                  }}
                >
                  <span>Cree le {new Date(canvas.createdAt).toLocaleDateString('fr-FR')}</span>
                </div>
              </Link>
              <button
                onClick={(e) => {
                  e.preventDefault();
                  setDeleteCanvasId(canvas.id);
                }}
                title="Supprimer ce canvas"
                style={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  width: 24,
                  height: 24,
                  borderRadius: '50%',
                  backgroundColor: 'transparent',
                  border: 'none',
                  color: 'var(--color-text-muted)',
                  cursor: 'pointer',
                  fontSize: 16,
                  lineHeight: 1,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#EF444422';
                  e.currentTarget.style.color = '#EF4444';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = 'var(--color-text-muted)';
                }}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      <ConfirmDialog
        open={deleteCanvasId !== null}
        title="Supprimer ce canvas ?"
        message={
          <>
            <p>
              Le canvas <strong>{deleteCanvasTarget?.name ?? ''}</strong> et tous ses noeuds /
              liaisons / commentaires / snapshots seront supprimés définitivement.
            </p>
            <p style={{ marginTop: 8 }}>Cette action est irréversible.</p>
          </>
        }
        confirmLabel="Supprimer"
        destructive
        busy={deleteBusy}
        onConfirm={handleDeleteCanvas}
        onCancel={() => setDeleteCanvasId(null)}
      />

      <ConfirmDialog
        open={deleteWsOpen}
        title="Supprimer le workspace ?"
        message={
          <>
            <p>
              Le workspace <strong>{currentWorkspace?.name ?? ''}</strong> et{' '}
              <strong>tous ses canvas ({canvases.length})</strong> seront supprimés définitivement.
            </p>
            <p style={{ marginTop: 8 }}>Cette action est irréversible.</p>
          </>
        }
        confirmLabel="Supprimer définitivement"
        destructive
        busy={deleteBusy}
        onConfirm={handleDeleteWorkspace}
        onCancel={() => setDeleteWsOpen(false)}
      />
    </div>
  );
};

export default WorkspacePage;
